import type React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  RECIPE_TONE_TEXT,
  type RecipeTone,
} from '../interfaces/IReviewRecipe';
import type { AssistantModePickerOption } from './AssistantModePicker';

interface AssistantModeHeaderProps<TId extends string> {
  panelTitle: string;
  value: TId;
  options: readonly AssistantModePickerOption<TId>[];
  onValueChange: (value: TId) => void;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onClose: () => void;
  ariaLabel: string;
  closeAriaLabel: string;
  testId?: string;
  className?: string;
  /** Config detail for the ACTIVE option, rendered at the top of the open menu. */
  activeDetail?: React.ReactNode;
  /** Non-interactive status inside the trigger, visible when details are closed. */
  collapsedStatus?: React.ReactNode;
  /** Label shown between activeDetail and the option list. */
  optionsLabel?: string;
}

const TONE_BADGE_CLASS: Record<RecipeTone, string> = {
  red: 'bg-rose-50 text-rose-700',
  blue: 'bg-blue-50 text-blue-700',
  amber: 'bg-amber-50 text-amber-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  slate: 'bg-slate-100 text-slate-700',
};

const DEFAULT_TONE: RecipeTone = 'slate';

export function AssistantModeHeader<TId extends string>({
  panelTitle,
  value,
  options,
  onValueChange,
  menuOpen,
  onMenuOpenChange,
  onClose,
  ariaLabel,
  closeAriaLabel,
  testId,
  className,
  activeDetail,
  collapsedStatus,
  optionsLabel,
}: AssistantModeHeaderProps<TId>) {
  const activeOption =
    options.find((option) => option.id === value) ?? options[0];
  if (!activeOption) return null;

  const ActiveIcon = activeOption.icon;
  const activeTone = activeOption.tone ?? DEFAULT_TONE;

  return (
    <div
      className={cn('shrink-0 border-b border-border bg-background', className)}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => onMenuOpenChange(!menuOpen)}
          aria-label={ariaLabel}
          aria-expanded={menuOpen}
          data-testid={testId ? `${testId}.trigger` : undefined}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span
            className={cn(
              'inline-flex size-7 shrink-0 items-center justify-center rounded-md',
              TONE_BADGE_CLASS[activeTone],
            )}
          >
            <ActiveIcon className="size-3.5" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {panelTitle}
            </span>
            <span className="block truncate text-sm font-semibold leading-tight text-foreground">
              {activeOption.label}
            </span>
            {activeOption.summary ? (
              <span className="mt-0.5 block truncate text-[11px] leading-tight text-muted-foreground">
                {activeOption.summary}
              </span>
            ) : null}
            {!menuOpen && collapsedStatus ? (
              <span className="mt-1 block text-[10px] font-medium text-muted-foreground" data-testid={testId ? `${testId}.status` : undefined}>
                {collapsedStatus}
              </span>
            ) : null}
          </span>
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              menuOpen && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeAriaLabel}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>

      {menuOpen ? (
        <div className="max-h-[55vh] overflow-y-auto px-3 pb-3">
          {activeDetail ? <div className="mb-2">{activeDetail}</div> : null}
          {activeDetail && optionsLabel ? (
            <div className="mb-1 px-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {optionsLabel}
            </div>
          ) : null}
          <div className="space-y-1">
          {options.map((option) => {
            const isActive = option.id === value;
            const tone = option.tone ?? DEFAULT_TONE;
            const Icon = option.icon;

            return (
              <button
                key={option.id}
                type="button"
                disabled={option.disabled}
                aria-pressed={isActive}
                onClick={() => {
                  if (!isActive) onValueChange(option.id);
                  onMenuOpenChange(false);
                }}
                className={cn(
                  'flex w-full items-start gap-2 rounded-md border px-2.5 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  isActive
                    ? 'border-primary/45 bg-primary/5'
                    : 'border-border bg-background hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <span className={cn('mt-0.5 shrink-0', RECIPE_TONE_TEXT[tone])}>
                  <Icon className="size-3.5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] font-semibold leading-tight text-foreground">
                    {option.label}
                  </span>
                  <span className="mt-0.5 block text-[10.5px] leading-snug text-muted-foreground">
                    {option.description}
                  </span>
                </span>
              </button>
            );
          })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
